import {
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  useIonViewWillEnter,
} from "@ionic/react";
import { useContext, useState } from "react";
import { useParams } from "react-router";
import { TranslationsContext } from "../../util/Translations";
import { useSettingsStore } from "../../store/settings";
import { DEFAULT_CYCLE_COUNT_ITEM_LIMIT } from "../../hooks/useDownloadCycleCountsForOfflineAccess";
import DefaultWOQuery from "../../components/DefaultWOQuery";
import DefaultEquipmentQuery from "../../components/DefaultEquipmentQuery";
import DefaultInventoryQuery from "../../components/DefaultInventoryQuery";
import DefaultRequisitionQuery from "../../components/DefaultRequisitionQuery";
import DefaultCycleCountQuery from "../../components/DefaultCycleCountQuery";
import DefaultEmployeeQuery from "../../components/DefaultEmployeeQuery";
import DefaultStoreLocation from "../../components/DefaultStoreLocation";
import DefaultFilterForIssueScreen from "../../components/DefaultFilterForIssueScreen";
import CycleCountItemLimit from "../../components/CycleCountItemLimit";
import SmsPhoneCodesSetting from "../../components/SmsPhoneCodesSetting";
import {
  QUERY_SECTIONS,
  ReadOnlyNote,
  SETTINGS_ROOT,
  SettingsSubPage,
  readDefaultQuery,
  useEamSettingsReadOnly,
} from "./settingsShared";

export const MODULE_DEFAULTS_ROOT = `${SETTINGS_ROOT}/module-defaults`;

export type ModuleDefaults = Record<keyof typeof QUERY_SECTIONS, string>;

/** Default query names per module ("" when not set). */
export const readModuleDefaults = async (): Promise<ModuleDefaults> => {
  const keys = Object.keys(QUERY_SECTIONS) as (keyof typeof QUERY_SECTIONS)[];
  const values = await Promise.all(
    keys.map((key) => readDefaultQuery(QUERY_SECTIONS[key]).catch(() => ""))
  );
  return keys.reduce(
    (result, key, index) => ({ ...result, [key]: values[index] }),
    {} as ModuleDefaults
  );
};

const MODULES = [
  { key: "work-orders", titleKey: "lbl_mob_module_work_orders", title: "Work orders" },
  { key: "equipment", titleKey: "lbl_mob_module_equipment", title: "Equipment" },
  {
    key: "inventory",
    titleKey: "lbl_mob_module_inventory_requisitions",
    title: "Inventory and requisitions",
  },
  { key: "cycle-count", titleKey: "lbl_mob_module_cycle_count", title: "Cycle count" },
  { key: "employees", titleKey: "lbl_mob_module_employees", title: "Employees" },
];

const ModuleDefaultsSettings: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const readOnly = useEamSettingsReadOnly();
  const itemLimit = useSettingsStore((state) => state.cycleCountItemLimit);
  const phoneCodes = useSettingsStore((state) => state.smsCapablePhoneCodes);
  const [defaults, setDefaults] = useState<ModuleDefaults>();

  useIonViewWillEnter(() => {
    if (!readOnly) readModuleDefaults().then(setDefaults);
  }, [readOnly]);

  const noQuery = translations["lbl_mob_no_query_set"] || "No query set";
  const queryOrEmpty = (value?: string) => value || noQuery;

  const summaries: Record<string, string> = {
    "work-orders": queryOrEmpty(defaults?.workOrder),
    equipment: queryOrEmpty(defaults?.equipment),
    inventory: [defaults?.inventory, defaults?.requisition]
      .filter(Boolean)
      .join(" · ") || noQuery,
    "cycle-count": `${queryOrEmpty(defaults?.cycleCount)} · ${
      translations["lbl_mob_limit"] || "Limit"
    } ${itemLimit ?? DEFAULT_CYCLE_COUNT_ITEM_LIMIT}`,
    employees: [
      queryOrEmpty(defaults?.employee),
      phoneCodes?.length
        ? `${phoneCodes.length} ${
            translations["lbl_mob_text_enabled_types"] || "text-enabled types"
          }`
        : "",
    ]
      .filter(Boolean)
      .join(" · "),
  };

  return (
    <SettingsSubPage
      title={translations["lbl_mob_settings_module_defaults"] || "Module defaults"}
    >
      {readOnly && <ReadOnlyNote />}
      <IonList inset className="settings-list" style={{ marginTop: 16 }}>
        {MODULES.map((module) => (
          <IonItem
            key={module.key}
            button
            detail
            routerLink={`${MODULE_DEFAULTS_ROOT}/${module.key}`}
          >
            <IonLabel>
              <h2>{translations[module.titleKey] || module.title}</h2>
              <p className="settings-subtext">
                {defaults || readOnly ? summaries[module.key] : "…"}
              </p>
            </IonLabel>
          </IonItem>
        ))}
      </IonList>
    </SettingsSubPage>
  );
};

/** One module's defaults; EAM-backed controls are disabled while offline. */
export const ModuleDefaultDetail: React.FC = () => {
  const { module } = useParams<{ module: string }>();
  const { translations } = useContext(TranslationsContext);
  const readOnly = useEamSettingsReadOnly();
  const definition = MODULES.find((m) => m.key === module);
  const title = definition
    ? translations[definition.titleKey] || definition.title
    : translations["lbl_mob_settings_module_defaults"] || "Module defaults";

  return (
    <SettingsSubPage title={title} backHref={MODULE_DEFAULTS_ROOT}>
      {readOnly && <ReadOnlyNote />}
      <IonList inset className="settings-list" style={{ marginTop: 16 }}>
        {module === "work-orders" && <DefaultWOQuery disabled={readOnly} />}
        {module === "equipment" && <DefaultEquipmentQuery disabled={readOnly} />}
        {module === "inventory" && (
          <>
            <DefaultInventoryQuery disabled={readOnly} />
            <DefaultRequisitionQuery disabled={readOnly} />
            <DefaultStoreLocation disabled={readOnly} />
            <DefaultFilterForIssueScreen disabled={readOnly} />
          </>
        )}
        {module === "cycle-count" && (
          <>
            <DefaultCycleCountQuery disabled={readOnly} />
            <CycleCountItemLimit />
          </>
        )}
        {module === "employees" && (
          <>
            <DefaultEmployeeQuery disabled={readOnly} />
            <SmsPhoneCodesSetting />
          </>
        )}
      </IonList>
      {!definition && (
        <IonNote className="settings-readonly-note">
          {translations["lbl_mob_no_settings"] || "No settings."}
        </IonNote>
      )}
    </SettingsSubPage>
  );
};

export default ModuleDefaultsSettings;
